import { KeyRound } from 'lucide-react';
import { ModelProviderSecretPicker } from '@reflex/ui/components/model-providers/ModelProviderSecretPicker';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import type { Agent } from '@/lib/types';
import { useSubscriptionSwitch } from './useSubscriptionSwitch';

/**
 * Lets a user re-point a running agent at a different model-provider
 * subscription. The swap takes effect on the agent's next turn (the server
 * rewrites the on-box credential immediately when the box is running, otherwise
 * on the next resume). Renders nothing for agent types without subscription
 * auth or for agents that can no longer take a turn. The chat-input toolbar
 * exposes the same switch via its key icon (see {@link useSubscriptionSwitch}).
 */
export function AgentSubscriptionSelector({ agent }: { agent: Agent }) {
  const { canSwitch, provider, affordance, manage, dialog, onChange, refresh, pending } =
    useSubscriptionSwitch(agent);

  if (!canSwitch || !provider) return null;

  return (
    <div className="flex flex-col gap-1.5 pt-1" data-testid="agent-subscription-selector">
      <SectionLabel className="pl-7">Subscription</SectionLabel>
      <div className="pl-7">
        <ModelProviderSecretPicker
          value={agent.modelProviderSecretId ?? null}
          onChange={onChange}
          provider={provider}
          allowedTypes={['subscription']}
          addSubscription={affordance}
          refreshSelected={refresh}
          manageSubscription={manage}
          disabled={pending}
          owner={agent.userId ? { ownerId: agent.userId } : undefined}
          // Switching writes to the server, so only explicit picks may fire.
          autoSelectDefault={false}
        />
        {/* Set by a switch or a refresh the box was too suspended to take, so
            the copy names neither: what the user needs to know is that the
            credential is not on the box yet. */}
        {agent.credentialSyncPending ? (
          <p className="pt-1 text-xs text-muted-foreground italic">
            <KeyRound className="mr-1 inline size-3" aria-hidden />
            Subscription applies on the next turn.
          </p>
        ) : null}
      </div>
      {dialog}
    </div>
  );
}
